import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CONFIG } from '../../../assets/config';
import { environment } from '../../../environments/environment';
import { ClaimStep } from '../models/claim-step';
import { ConfirmAppointmentRequest } from '../models/dto/confirm-appointment-request';
import { ConfirmAppointmentResponse } from '../models/dto/confirm-appointment-response';
import { EntranceTreesRequest } from '../models/dto/entrance-trees-request';
import {
	DamageCause,
	DamageDetail,
	DamageType,
	EntranceTreesResponse,
	Group,
	OtherDamage,
	OtherthirdHarm,
	OwnDamage,
	ThirdHarm
} from '../models/dto/entrance-trees-response';
import { GetClientRequest } from '../models/dto/get-client-request';
import { GetClientResponse, Insured } from '../models/dto/get-client-response';
import { GetDecisionTreeRequest } from '../models/dto/get-decision-tree-request';
import { GetDecisionTreeResponse, GetDecisionTreeResult } from '../models/dto/get-decision-tree-response';
import { GetTimesZoneRequest } from '../models/dto/get-times-zone-request';
import { GetTimesZoneResponse, GetTimesZoneResult, Timeszone } from '../models/dto/get-times-zone-response';
import {
	ContactInfo,
	GuarenteeInfo,
	OcurrencesInfo,
	OpenClaimRequest,
	SondeoInfo
} from '../models/dto/open-claim-request';
import { OpenClaimResponse } from '../models/dto/open-claim-response';
import { RootResponse } from '../models/dto/root-response';
import { SendDocumentsRequest } from '../models/dto/send-documents-request';
import { SendDocumentsResponse } from '../models/dto/send-documents-response';
import { Language } from '../models/language';
import { MasterDataService } from './master-data.service';
import { StorageService } from './storage.service';
import { ServiceErrorStatus } from '../models/dto/service-error';
import { UrlParamsService } from '../../core/services/url-params.service';

@Injectable({
	providedIn: 'root'
})
export class HomeserveService {
	private currentInsured?: Insured;

	private currentDecisionTree?: GetDecisionTreeResult;

	private currentCauseGroupsEntranceTree?: EntranceTreesResponse | undefined;
	private currentDamageTypesEntranceTree?: EntranceTreesResponse | undefined;
	private currentDamageDetailsEntranceTree?: EntranceTreesResponse | undefined;
	private currentDamageCausesEntranceTree?: EntranceTreesResponse | undefined;
	private currentDamagesInfoEntranceTree?: EntranceTreesResponse | undefined;

	private currentOpenClaim?: OpenClaimResponse | ServiceErrorStatus | undefined;

	public readonly defaultLanguage: Language = this.masterService.getLanguages()[0];

	private selectedLanguage: Language = this.defaultLanguage;

	public claimStep: ClaimStep = ClaimStep.ContactData;
	otherListInfo = [];
  urlParams: any = {}

	/**
	 * Constructor del servicio en el que se inicializan las cabeceras que se utilizarán en la comunicación con la API
	 * @param http propiedad que se inicializa para realizar operaciones HTTP
	 */
	constructor(
		private http: HttpClient,
		private storageService: StorageService,
		private masterService: MasterDataService,
		private translate: TranslateService,
		private urlParamsService: UrlParamsService
	) {
		this.translate.onLangChange.subscribe((langEvent: LangChangeEvent) => {
			if (this.selectedLanguage.i18nKey !== langEvent.lang) {
				this.setSelectedLanguage(
					this.masterService.getLanguages().find((lang) => lang.i18nKey === langEvent.lang) ?? this.defaultLanguage
				);
			}
		});
		this.urlParams = this.storageService.get('urlParams');
	}

	/**
	 * Getters/setters
	 */

	public setSelectedInsured(insured?: Insured) {
		this.currentInsured = insured;
	}

	public getCurrentInsured(): Insured | undefined {
		return this.currentInsured;
	}

	public getCurrentDecisionTree(): GetDecisionTreeResult | undefined {
		return this.currentDecisionTree;
	}

	public getCurrentCauseGroupsEntranceTree(): EntranceTreesResponse | undefined {
		return this.currentCauseGroupsEntranceTree;
	}
	public getCurrentDamageTypesEntranceTree(): EntranceTreesResponse | undefined {
		return this.currentDamageTypesEntranceTree;
	}
	public getCurrentDamageDetailsEntranceTree(): EntranceTreesResponse | undefined {
		return this.currentDamageDetailsEntranceTree;
	}
	public getCurrentDamageCausesEntranceTree(): EntranceTreesResponse | undefined {
		return this.currentDamageCausesEntranceTree;
	}
	public getCurrentDamagesInfoEntranceTree(): EntranceTreesResponse | undefined {
		return this.currentDamagesInfoEntranceTree;
	}

	public getCurrentOpenClaim() {
		return this.currentOpenClaim !== undefined && "openClaimResult" in this.currentOpenClaim ? this.currentOpenClaim : undefined;
	}

	public setSelectedLanguage(language: Language) {
		this.selectedLanguage = language;
		this.translate.use(language.i18nKey);
	}

	public setOthersListInfo(otherListInfo: any) {
		this.otherListInfo = otherListInfo;
	}

	public getSelectedLanguage(): Language {
		return this.selectedLanguage;
	}

	public isOnlyIndemnizable(): boolean {
		return (
			(this.currentDecisionTree?.agentClaimTypeListInfo?.length === 1 &&
				this.currentDecisionTree?.agentClaimTypeListInfo[0].agentClaimType === 'Indemnizable') ||
			this.currentDecisionTree?.claimType === '3'
		);
	}

	/**
	 * HTTP methods
	 */

	getClient(clientRequest: GetClientRequest): Observable<Insured[]> {
		return this.http.post<RootResponse>(`${environment.apiURL}${environment.endpoints.getClient}`, clientRequest).pipe(
			map((response: RootResponse) => {
				if((response.value as GetClientResponse)?.getClientResult?.typeError){
					this.masterService.manageGetClientControlledErrors((response.value as GetClientResponse)?.getClientResult);
				}
				const result = (response.value as GetClientResponse)?.getClientResult?.insuredInfo;
				return result;
			})
		);
	}

	refreshInsured(ocurrenceDate?: string): Promise<Insured> {
		const request: GetClientRequest = {
			ocurrenceDate: ocurrenceDate ?? '',
			dni: this.currentInsured?.dni ?? '',
			policy: this.currentInsured?.policy,
			auditoryInfo: {
				originCall: CONFIG.originCall,
				transactionId: crypto.randomUUID()
			},
			company: CONFIG.companyId
		};
		return new Promise((resolve, reject) => {
			this.getClient(request).subscribe({
				next: (response) => {
					this.currentInsured = response[0] ?? undefined;
					resolve(this.currentInsured);
				},
				error: (err) => reject(err)
			});
		});
	}

	getCauseGroupsLanguage(): Group[] {
		return (
			this.currentCauseGroupsEntranceTree?.entranceTreesResult?.causeGroupInfo?.languageInfo.find(
				(language) => language.languageId === this.selectedLanguage.id
			)?.groupInfo ?? []
		);
	}

	getCauseGroups(entranceTreesRequest: EntranceTreesRequest): Observable<Group[]> {
		return this.http
			.post<RootResponse>(`${environment.apiURL}${environment.endpoints.entranceTrees}`, entranceTreesRequest)
			.pipe(
				map((res: RootResponse) => {
					this.currentCauseGroupsEntranceTree = res.value as EntranceTreesResponse;
					return this.getCauseGroupsLanguage();
				}),
				catchError(this.handleError)
			);
	}

	getDamageTypesLanguage(): DamageType[] {
		return (
			this.currentDamageTypesEntranceTree?.entranceTreesResult.causeGroupInfo.languageInfo.find(
				(language) => language.languageId === this.selectedLanguage.id
			)?.groupInfo[0].damageTypeInfo ?? []
		);
	}

	getDamageTypes(entranceTreesRequest: EntranceTreesRequest): Observable<DamageType[]> {
		return this.http
			.post<RootResponse>(`${environment.apiURL}${environment.endpoints.entranceTrees}`, entranceTreesRequest)
			.pipe(
				map((res: RootResponse) => {
					this.currentDamageTypesEntranceTree = res.value as EntranceTreesResponse;
					return this.getDamageTypesLanguage();
				}),
				catchError(this.handleError)
			);
	}

	getDamageDetailsLanguage(): DamageDetail[] {
		return (
			this.currentDamageDetailsEntranceTree?.entranceTreesResult.causeGroupInfo.languageInfo.find(
				(language) => language.languageId === this.selectedLanguage.id
			)?.groupInfo[0]?.damageTypeInfo[0]?.damageDetailInfo ?? []
		);
	}

	getDamageDetails(entranceTreesRequest: EntranceTreesRequest): Observable<DamageDetail[]> {
		return this.http
			.post<RootResponse>(`${environment.apiURL}${environment.endpoints.entranceTrees}`, entranceTreesRequest)
			.pipe(
				map((res: RootResponse) => {
					this.currentDamageDetailsEntranceTree = res.value as EntranceTreesResponse;
					return this.getDamageDetailsLanguage();
				}),
				catchError(this.handleError)
			);
	}

	getDamageCausesLanguage(): DamageCause[] {
		return (
			this.currentDamageCausesEntranceTree?.entranceTreesResult.causeGroupInfo.languageInfo.find(
				(language) => language.languageId === this.selectedLanguage.id
			)?.groupInfo[0]?.damageTypeInfo[0]?.damageDetailInfo[0]?.damageCauseInfo ?? []
		);
	}

	getDamageCauses(entranceTreesRequest: EntranceTreesRequest): Observable<DamageCause[]> {
		return this.http
			.post<RootResponse>(`${environment.apiURL}${environment.endpoints.entranceTrees}`, entranceTreesRequest)
			.pipe(
				map((res: RootResponse) => {
					this.currentDamageCausesEntranceTree = res.value as EntranceTreesResponse;
					return this.getDamageCausesLanguage();
				}),
				catchError(this.handleError)
			);
	}

	getDamageInfoLanguage(): DamageCause {
		const damageCause: DamageCause =
			this.currentDamageCausesEntranceTree?.entranceTreesResult.causeGroupInfo.languageInfo.find(
				(language) => language.languageId === this.selectedLanguage.id
			)?.groupInfo[0]?.damageTypeInfo[0]?.damageDetailInfo[0]?.damageCauseInfo[0] as DamageCause;
		if (damageCause) {
			this.initializeDamageIds(damageCause);
		}
		return damageCause;
	}

	getDamageInfo(entranceTreesRequest: EntranceTreesRequest): Observable<DamageCause> {
		return this.http
			.post<RootResponse>(`${environment.apiURL}${environment.endpoints.entranceTrees}`, entranceTreesRequest)
			.pipe(
				map((res: RootResponse) => {
					this.cleanEmptyDamages(res);
					this.currentDamageCausesEntranceTree = res.value as EntranceTreesResponse;
					return this.getDamageInfoLanguage();
				}),
				catchError(this.handleError)
			);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private cleanEmptyDamages(res: any) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		res.value.entranceTreesResult.causeGroupInfo.languageInfo.forEach((language: any) => {
			const damageCause = language.groupInfo[0]?.damageTypeInfo[0]?.damageDetailInfo[0].damageCauseInfo[0];
			if (damageCause?.damageInfo?.ownDamagesInfo) {
				damageCause.damageInfo.ownDamagesInfo = damageCause.damageInfo.ownDamagesInfo.filter(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(damage: any) => {
						return damage.ownDamage;
					}
				);
			}
			if (damageCause?.damageInfo?.otherDamagesInfo) {
				damageCause.damageInfo.otherDamagesInfo = damageCause.damageInfo.otherDamagesInfo.filter(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(damage: any) => {
						return damage.otherDamage;
					}
				);
			}

			if (damageCause?.thirdHarmsInfo?.thirdHarmInfo) {
				damageCause.thirdHarmsInfo.thirdHarmInfo = damageCause.thirdHarmsInfo.thirdHarmInfo.filter(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(damage: any) => {
						return damage.thirdHarm;
					}
				);
			}

			if (damageCause?.thirdHarmsInfo?.otherthirdHarmInfo) {
				damageCause.thirdHarmsInfo.otherthirdHarmInfo = damageCause.thirdHarmsInfo.otherthirdHarmInfo.filter(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(damage: any) => {
						return damage.otherthirdHarm;
					}
				);
			}
		});
	}

	private initializeDamageIds(damageCause: DamageCause) {
		if (damageCause?.damageInfo?.ownDamagesInfo) {
			let index = 1;
			damageCause.damageInfo.ownDamagesInfo.forEach((damage: OwnDamage) => {
				damage.id = index.toString();
				index++;
			});
		}
		if (damageCause?.damageInfo?.otherDamagesInfo) {
			let index = 1;
			damageCause.damageInfo.otherDamagesInfo.forEach((damage: OtherDamage) => {
				damage.id = index.toString();
				index++;
			});
		}
		if (damageCause?.thirdHarmsInfo?.thirdHarmInfo) {
			let index = 1;
			damageCause.thirdHarmsInfo.thirdHarmInfo.forEach((damage: ThirdHarm) => {
				damage.id = index.toString();
				index++;
			});
		}
		if (damageCause?.thirdHarmsInfo?.otherthirdHarmInfo) {
			let index = 1;
			damageCause.thirdHarmsInfo.otherthirdHarmInfo.forEach((damage: OtherthirdHarm) => {
				damage.id = index.toString();
				index++;
			});
		}
	}

	getDecisionTree(request: GetDecisionTreeRequest): Observable<GetDecisionTreeResult> {
		return this.http.post<RootResponse>(`${environment.apiURL}${environment.endpoints.getDecisionTree}`, request).pipe(
			map((res: RootResponse) => {
				this.currentDecisionTree = (res.value as GetDecisionTreeResponse).getDecisionTreeResult;
				return this.currentDecisionTree;
			}),
			catchError(this.handleError)
		);
	}

	getTimesZone(timesZoneRequest: GetTimesZoneRequest): Observable<GetTimesZoneResult> {
		return this.http
			.post<RootResponse>(`${environment.apiURL}${environment.endpoints.getTimesZone}`, timesZoneRequest)
			.pipe(
				map((res: RootResponse) => {
					const timesZoneResponse = res.value as GetTimesZoneResponse;
					return timesZoneResponse.getTimesZoneResult as GetTimesZoneResult;
				}),
				catchError(this.handleError)
			);
	}

	confirmAppointment(confirmAppointmentRequest: ConfirmAppointmentRequest): Observable<ConfirmAppointmentResponse | ServiceErrorStatus> {
		return this.http
			.post<RootResponse>(`${environment.apiURL}${environment.endpoints.confirmAppointment}`, confirmAppointmentRequest)
			.pipe(
				map((res: RootResponse) => {
					if((res.value as ServiceErrorStatus).statusCode !== undefined) {
						return res.value as ServiceErrorStatus;
					} else {
						return res.value as ConfirmAppointmentResponse;
					}

				}),
				catchError(this.handleError)
			);
	}

	openClaim(request: OpenClaimRequest): Observable<OpenClaimResponse | ServiceErrorStatus> {
		return this.http.post<RootResponse>(`${environment.apiURL}${environment.endpoints.openClaim}`, request).pipe(
			map((res: RootResponse) => {
				if((res.value as ServiceErrorStatus).statusCode !== undefined) {
					this.currentOpenClaim = res.value as ServiceErrorStatus;
				} else {
					this.currentOpenClaim = res.value as OpenClaimResponse;
				}
				return this.currentOpenClaim;
			}),
			catchError(this.handleError)
		);
	}

	sendDocuments(request: SendDocumentsRequest): Observable<SendDocumentsResponse | ServiceErrorStatus> {
		return this.http.post<RootResponse>(`${environment.apiURL}${environment.endpoints.sendDocuments}`, request).pipe(
			map((res: RootResponse) => {
				if((res.value as ServiceErrorStatus).statusCode !== undefined) {
					return res.value as ServiceErrorStatus;
				} else {
					return res.value as SendDocumentsResponse;
				}
			}),
			catchError(this.handleError)
		);
	}

	/**
	 * Maneja cualquier error del lado cliente o servidor que pueda darse durante la comunicación con la API
	 * @param {any} error del lado cliente o servidor que pueda darse durante la comunicación con la API
	 */
	handleError(error: any): Observable<never> {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// client-side error
			errorMessage = `Error: ${error.error.message}`;
		} else {
			// server-side error
			if (error.status === 404 || 0) {
				errorMessage = 'No se pudo conectar con el servidor';
			} else {
				errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
			}
		}
		//    window.alert(errorMessage);
		return throwError(errorMessage);
	}

	/**
	 * REQUESTS
	 *
	 */

	public createDecisionTreeRequest(): GetDecisionTreeRequest {
		const claimData: any = this.storageService.get(this.storageService.claimDataKey);
		const guaranteeList = this.getCurrentInsured()
			?.guranteeListInfo.map((g) => g.code)
			.join(',');
		return {
			companyId: CONFIG.companyId,
			parametrizationType: CONFIG.parametrizationType,
			productId: this.getCurrentInsured()?.productid ?? '',
			riskTypeId: CONFIG.riskTypeId,
			causeGroupId: claimData.claimDetails?.claimCauseGroup?.causeGroupId ?? '1',
			damageCauseId: claimData.claimDetails?.claimCause?.damageCauseId ?? '1',
			damageDetailId: claimData.claimDetails.claimDamageDetail?.damageDetailId ?? '1',
			damageTypeId: claimData.claimDetails.claimDamageType?.damageTypeId ?? '1',
			languageId: this.defaultLanguage.id,
			hasOwnHarm:
				claimData?.causerData?.claimOwnDamages?.length > 0 || claimData?.causerData?.claimOtherDamages?.length > 0
					? 'S'
					: 'N',
			ownHarmList:
				claimData?.causerData?.claimOwnDamages?.length > 0
					? claimData?.causerData?.claimOwnDamages
							.map((damage: { ownDamage: OwnDamage }) => damage.ownDamage.ownDamage)
							.join(';')
					: '',
			otherOwnHarmList:
				claimData?.causerData?.claimOtherDamages?.length > 0
					? claimData?.causerData?.claimOtherDamages
							?.map((damage: { otherDamage: OtherDamage }) => damage.otherDamage.otherDamage)
							.join(';')
					: '',
			hasThirdHarm:
				claimData?.thirdDamagedData?.thirdOwnDamages?.length > 0 ||
				claimData.thirdDamagedData?.thirdOtherDamages?.length > 0
					? 'S'
					: 'N',
			harmedThirdList:
				claimData?.thirdDamagedData?.thirdOwnDamages?.length > 0
					? claimData?.thirdDamagedData?.thirdOwnDamages
							.map((damage: { thirdHarm: ThirdHarm }) => damage.thirdHarm.thirdHarm)
							.join(';')
					: '',
			otherHarmedThirdList:
				claimData?.thirdDamagedData?.thirdOtherDamages?.length > 0
					? claimData?.thirdDamagedData?.thirdOtherDamages
							.map((damage: { otherthirdHarm: OtherthirdHarm }) => damage.otherthirdHarm.otherthirdHarm)
							.join(';')
					: '',
			guaranteeList: guaranteeList ?? '',
			rcGuaranteeList: '',
			auditoryInfo: {
				originCall: CONFIG.originCall,
				transactionId: crypto.randomUUID()
			}
		};
	}

	public getOpenClaimRequest(setReasonWrongAddress:boolean=false): OpenClaimRequest {
		this.storageService.clear('terms');
		const insured = this.getCurrentInsured();
		const decision = this.getCurrentDecisionTree();
		const currentDate = `${new Date().getFullYear()}${(new Date().getMonth() + 1)
			.toString()
			.padStart(2, '0')}${new Date().getDate().toString().padStart(2, '0')}`;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const contactData: any = this.storageService.get(this.storageService.contactDataKey);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const claimData: any = this.storageService.get(this.storageService.claimDataKey);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const confirmData: any = this.storageService.get(this.storageService.confirmDataKey);
		const occurenceDate =
			this.claimStep >= ClaimStep.ClaimData
				? claimData?.claimDate
					? `${claimData?.claimDate?.year}${claimData?.claimDate?.month
							.toString()
							.padStart(2, '0')}${claimData?.claimDate?.day.toString().padStart(2, '0')}`
					: ''
				: '';

		const guaranteeInfo: GuarenteeInfo[] = [];
		if (this.claimStep >= ClaimStep.ClaimData) {
			decision?.guaranteeListInfo?.forEach((guarantee) => {
				if (guarantee?.code.length === 8) {
					guaranteeInfo.push({
						capitalId: guarantee.code.substring(4),
						guarenteeId: guarantee.code.substring(0, 4)
					});
				}
			});
		}

		const indemnizable =
			this.claimStep >= ClaimStep.ClaimData
				? decision?.agentClaimTypeListInfo?.find((claimValue) =>
						this.masterService.getIndemnizableKeys().find((value) => value === claimValue.agentClaimType)
				  ) ?? false
				: false;
		const ocurrences: OcurrencesInfo[] =
			this.claimStep >= ClaimStep.ConfirmClaim
				? !this.isOnlyIndemnizable() &&
				this.masterService.isValidTechnicianKeyToSendDevices(confirmData?.technician) &&
				  claimData.claimDetails.damagedDevices?.length > 0
					? claimData.claimDetails.damagedDevices.map((device: any) => ({
							TradeId: '',
							ocurrenceId: '',
							TradeExternalId: '',
							OcurrenceExternalId: decision?.eventListInfo[0].events ?? '',
							TradeDesc: confirmData.technician,
							ocurrenceDesc: '',
							DeviceId: device.device.code,
							BrandId: device.brand.code
					  }))
					: [
							{
								TradeId: '',
								ocurrenceId: '',
								TradeExternalId: '',
								OcurrenceExternalId: decision?.eventListInfo ? decision?.eventListInfo[0].events ?? '' : '',
								TradeDesc: confirmData?.technician,
								ocurrenceDesc: '',
								DeviceId: '',
								BrandId: ''
							}
					  ]
				: [];

		const declarantInfo = contactData.primaryData.policyHolder
			? {
					declarantChannel: CONFIG.declarantChannel,
					declarantName: insured?.name ?? '',
					declarantSurname: insured?.surname ?? '',
					declarantSecondSurname: insured?.secondSurname ?? '',
					declarantType: '00'
			  }
			: {
					declarantChannel: CONFIG.declarantChannel,
					declarantName: contactData.declarant.name,
					declarantSurname: contactData.declarant.surname,
					declarantSecondSurname: contactData.declarant.secondSurname,
					declarantType: contactData.declarant.relationship.code
			  };

		const sondeo: SondeoInfo = Object.assign(
			{
				agentClaimType: confirmData?.sendFixer ? (confirmData.isExistsRepair ? 'Mixto' : 'Reparable') : 'Indemnizable'
			},
			decision
		);
		const contacts: ContactInfo[] = [];
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		contactData?.contactData?.contacts.forEach((contact: any) =>
			contacts.push({
				contactType: contact.isInsured ? '02' : '99',
				isMain: contact.isPrimaryContact ? 'S' : 'N',
				email: contactData?.contactData?.email.toLowerCase(),
				phoneInfo: {
					numPhone: contact.phone,
					prefixPhone: contact.prefixPhone,
					typePhone: contact.typePhone.code
				},
				startTimeSlot: CONFIG.startTimeSlot,
				endTimeSlot: CONFIG.endTimeSlot,
				preferredChannel: contactData.contactData.contactByEmail ? '01' : '00'
			})
		);


			let compensableDescriptionValue = indemnizable ? claimData.claimDescription : '';
			if(setReasonWrongAddress && contactData?.primaryData?.reasonWrongAddress === 'a'){
				compensableDescriptionValue = compensableDescriptionValue + " | " + "Calle, portal, piso, código postal o localidad";
			}


		return {
			typeCall:
				this.masterService.getClaimConfig().find((claim) => claim.claimType === decision?.claimType)?.typeCall ?? '',
			company: CONFIG.companyId,
			policyexternalId: insured?.policy ?? this.urlParams.data,
			claimReparableDesc: this.claimStep >= ClaimStep.ClaimData ? this.mountClaimReparableDesc(claimData) : '',
			declarationDate: currentDate,
			claimOcurrenceDate: occurenceDate,
			isUrgent:
			 	!confirmData ? (contactData.isUrgent === true ? 'S' : 'N') :
				this.claimStep >= ClaimStep.ConfirmClaim ? (confirmData?.emergency ? 'S' : 'N') : '',
			languageId: this.defaultLanguage.id,
			groupClaim:
				this.claimStep >= ClaimStep.ClaimData ? claimData?.claimDetails?.claimCauseGroup?.causeGroupName ?? '' : '',
			damageType:
				this.claimStep >= ClaimStep.ClaimData ? claimData?.claimDetails?.claimDamageType?.damageTypeDesc ?? '' : '',
			damageDetail:
				this.claimStep >= ClaimStep.ClaimData ? claimData?.claimDetails?.claimDamageDetail?.damageDetailDesc ?? '' : '',
			damageCause:
				this.claimStep >= ClaimStep.ClaimData ? claimData?.claimDetails?.claimCause?.damageCauseDesc ?? '' : '',
			isExistOwnDamage:
				this.claimStep >= ClaimStep.ClaimData
					? claimData?.causerData?.claimOwnDamages?.length > 0 || claimData?.causerData?.claimOtherDamages?.length > 0
						? 'S'
						: 'N'
					: '',

			damagesInfo: this.claimStep >= ClaimStep.ClaimData ? claimData?.causerData?.claimOwnDamages ?? [] : [],
			isExistOtherDamage:
				this.claimStep >= ClaimStep.ClaimData
					? claimData?.thirdDamagedData?.thirdOwnDamages?.length > 0 ||
					  claimData?.thirdDamagedData?.thirdOtherDamages?.length > 0
						? 'S'
						: 'N'
					: '',
			damagesOtherInfo: this.claimStep >= ClaimStep.ClaimData ? claimData?.causerData?.claimOtherDamages ?? [] : [],
			IsExistAggrievedDamage: ocurrences?.length > 0 ? 'S' : 'N',
			ocurrencesInfo: ocurrences,
			dateInfo: {
				startDate: '',
				endDate: ''
			},
			phoneC2CInfo: {
				numPhone: '',
				prefixPhone: '',
				typePhone: ''
			},
			scheduleDate: '',
			declarantInfo: declarantInfo,
			contactInfo: contacts,
			guarenteeInfo: guaranteeInfo,
			liableInfo:
				this.claimStep >= ClaimStep.ClaimData && !claimData?.claimDetails?.insuredHome
					? {
							liablePersonData: {
								address: {
									city: '',
									country: '',
									infoAditional: '',
									province: '',
									roadName: '',
									roadNumber: '',
									roadType: '',
									zipCode: ''
								},
								email: claimData?.causerData?.causerEmail ?? '',
								firstSurname: claimData?.causerData?.causerSurname1 ?? '',
								name: claimData?.causerData?.causerName ?? '',
								nif: '',
								secondSurname: claimData?.causerData?.causerSurname2 ?? '',
								telephone: {
									numPhone: claimData?.causerData?.causerPhone ?? '',
									prefixPhone: claimData?.causerData?.causerPrefixPhone ?? '',
									typePhone: this.getTypePhone(claimData?.causerData?.causerPhone ?? null,claimData?.causerData?.causerPrefixPhone ?? null)
								},
								telephone2: {
									numPhone: '',
									prefixPhone: '',
									typePhone: this.getTypePhone(null,null)
								}
							},
							liablepersonDescription: '',
							liablePolicyInfo: {
								number: '',
								reference: ''
							},
							liablerelationship: claimData?.causerData?.causerRelationShip?.code ?? ''
					  }
					: '',
			injuredInfo:
				this.claimStep >= ClaimStep.ClaimData && claimData?.thirdDamagedData.radioThirdDamage
					? [
							{
								damagesAggrievedInfo: claimData?.thirdDamagedData?.thirdOwnDamages ?? [],
								damagesAggrievedOtherInfo: claimData?.thirdDamagedData?.thirdOtherDamages ?? [],
								injuredPersonData: {
									email: claimData?.thirdDamagedData?.thirdDamagedEmail,
									name: claimData?.thirdDamagedData?.thirdDamagedName,
									firstSurname: claimData?.thirdDamagedData?.thirdDamagedSurname1,
									secondSurname: claimData?.thirdDamagedData?.thirdDamagedSurname2,
									nif: '',
									telephone: {
										numPhone: claimData?.thirdDamagedData?.thirdDamagedPhone,
										prefixPhone: claimData?.thirdDamagedData?.thirdDamagedPrefixPhone,		
										typePhone: this.getTypePhone(claimData?.thirdDamagedData?.thirdDamagedPhone ?? null, claimData?.thirdDamagedData?.thirdDamagedPrefixPhone ?? null)
									},
									telephone2: {
										numPhone: '',
										prefixPhone: '',
										typePhone: this.getTypePhone(null,null)
									},
									address: {
										city: '',
										country: '',
										infoAditional: '',
										province: '',
										roadName: claimData?.thirdDamagedData?.thirdDamagedAddress ?? '',
										roadNumber: '',
										roadType: '',
										zipCode: ''
									}
								},
								injuredpersonDescription: claimData?.thirdDamagedData?.thirdDamagedRelationShip?.description ?? '',
								injuredPolicyInfo: {
									number: '',
									reference: ''
								},
								injuredrelationship: claimData?.thirdDamagedData?.thirdDamagedRelationShip?.code ?? ''
							}
					  ]
					: [],
			isExistsRepair: this.claimStep >= ClaimStep.ClaimData ? (this.isOnlyIndemnizable() ? 'N' : 'S') : '',
			auditoryInfo: {
				originCall: CONFIG.originCall,
				transactionId: crypto.randomUUID()
			},
			OriginExternalId: CONFIG.originExternalId,
			CauseGroupExternalId: this.claimStep >= ClaimStep.ClaimData ? decision?.causeGroupCia ?? '' : '',
			GuaranteeCauseExternalId: this.claimStep >= ClaimStep.ClaimData ? decision?.causeCia ?? '' : '',
			motivec2c: '',
			submotivec2c: '',
			riskType: CONFIG.riskTypeId,
			dni: insured?.dni ?? '',
			isProfOrRestoreHome:
				this.claimStep >= ClaimStep.ConfirmClaim ? (confirmData?.isProfOrRestoreHome ? 'S' : 'N') : '',
			compensableDescription: compensableDescriptionValue,
			waitingClaim: 'N',
			edaCode: '',
			actuationId: CONFIG.actuationId,
			sondeoInfo: this.claimStep >= ClaimStep.ClaimData ? sondeo : '',
			attachedDocumentation: '',
			attachedDocumentationListInfo: [],
			othersListInfo: this.otherListInfo
		};
	}

	public mountClaimReparableDesc(claimData: any) :string {
		const causerName = claimData?.causerData?.causerName ? ` + ${claimData?.causerData?.causerName}` : '',
			  causerSurname1 = claimData?.causerData?.causerSurname1 ? ` + ${claimData?.causerData?.causerSurname1}` : '',
			  causerSurname2 = claimData?.causerData?.causerSurname2 ? ` + ${claimData?.causerData?.causerSurname2}` : '',
			  causerPhone = claimData?.causerData?.causerPhone ? ` + ${claimData?.causerData?.causerPhone}` : '',
			  thirdDamagedName = claimData?.thirdDamagedData?.thirdDamagedName ? ` + ${claimData?.thirdDamagedData?.thirdDamagedName}` : '',
			  thirdDamagedSurname1 = claimData?.thirdDamagedData?.thirdDamagedSurname1 ? ` + ${claimData?.thirdDamagedData?.thirdDamagedSurname1}` : '',
			  thirdDamagedSurname2 = claimData?.thirdDamagedData?.thirdDamagedSurname2 ? ` + ${claimData?.thirdDamagedData?.thirdDamagedSurname2}` : '',
			  thirdDamagedEmail = claimData?.thirdDamagedData?.thirdDamagedEmail ? ` + ${claimData?.thirdDamagedData?.thirdDamagedEmail}` : '',
			  thirdDamagedPhone = claimData?.thirdDamagedData?.thirdDamagedPhone ? ` + ${claimData?.thirdDamagedData?.thirdDamagedPhone}` : '',
			  thirdDamagedAddress = claimData?.thirdDamagedData?.thirdDamagedAddress ? ` + ${claimData?.thirdDamagedData?.thirdDamagedAddress}` : '';
		return `${claimData?.claimDescription}${causerName}${causerSurname1}${causerSurname2}${causerPhone}${thirdDamagedName}${thirdDamagedSurname1}${thirdDamagedSurname2}${thirdDamagedEmail}${thirdDamagedPhone}${thirdDamagedAddress}`;
	}


	public getTypePhone(phone : any, prefixPhone : any) :string{
		let retorno = '';
		if(phone && prefixPhone){
			if(prefixPhone === '+34' || prefixPhone === '0034'){
				if(phone.toString().charAt(0) === '8' || phone.toString().charAt(0) === '9'){
					retorno = '1';
				} else {
					retorno = '2';
				}
			} else {
				retorno = '2';
			}
		}
		return retorno;
	}
}
